<template>
    <!-- PRODUCT-26110 Task 5 (INSTANCE_ATTRS_CLASS_STYLE): Vue 3 folds class/style into
         $attrs and stops auto-applying them to root when inheritAttrs is false, so a
         caller's `class="RecollectionIntervalCriterion__unitInput"` (etc.) would
         silently stop reaching this wrapper. Bind them explicitly to keep the old
         Vue 2 fallthrough behavior. -->
    <div class="CriterionEnumInput" :class="$attrs.class" :style="$attrs.style">
        <FormElement :label="hideTitle ? null : form.schema.title">
            <template v-if="form.schema.description" #labelHint>
                <div
                    v-tooltip="form.schema.description"
                    class="CriterionEnumInput__hintIcon"
                >
                    <FontAwesomeIcon :icon="faQuestionCircle" />
                </div>
            </template>
            <FormInput
                :invalid="showValidationErrors && (showError || accessed) && hasError"
                force-error-slot
                force-action-slot
            >
                <NewDropdown
                    class="CriterionEnumInput__dropdown"
                    :options="options"
                    :selected="currentOption"
                    :disabled="rbacDisabled"
                    @input="onInput"
                    @change="onChange"
                    @blur="touch"
                    @focus="onFocus"
                />
                <template v-if="showValidationErrors && (showError || accessed) && error" #errorMessage>
                    {{ error }}
                </template>
                <template v-if="rbacDisabled" #actionButtons>
                    <LockInputIcon />
                </template>
                <template v-else-if="deletable && !rbacDisabled" #actionButtons>
                    <NewButton
                        variant="flat"
                        size="icon-sm"
                        color="secondary"
                        class="JsonSchemaForm__deleteIcon"
                        @click="$emit('delete')"
                    >
                        <slot name="deleteIcon">
                            <EidIcon :icon-src="faXmark" alt="delete" />
                        </slot>
                    </NewButton>
                </template>
            </FormInput>
        </FormElement>
    </div>
</template>

<script lang="ts">
    import { Component, Watch } from '@evidentid/vue-property-decorator';

    import { faQuestionCircle } from '@fortawesome/free-regular-svg-icons';
    import { faXmark } from '@fortawesome/pro-light-svg-icons';
    import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

    import NewDropdown from '@evidentid/dashboard-commons/components/Dropdown/NewDropdown.vue';
    import { DropdownOption } from '@evidentid/dashboard-commons/components/Dropdown/types';
    import EidIcon from '@evidentid/dashboard-commons/components/EidIcon/EidIcon.vue';
    import { FormElement, FormInput } from '@evidentid/dashboard-commons/components/Form';
    import LockInputIcon from '@evidentid/dashboard-commons/components/LockInputIcon/LockInputIcon.vue';
    import { JsonFormBase } from '@evidentid/json-schema/interfaces/JsonForm';
    import { JsonSchemaEnum } from '@evidentid/json-schema/interfaces/JsonSchema';

    import NewButton from '@/components/new-button/NewButton.vue';

    import AbstractCriterionInput from './AbstractCriterionInput';

    @Component({
        emits: [ 'change', 'delete', 'input' ],
        components: { FormInput, NewDropdown, EidIcon, FontAwesomeIcon, FormElement, LockInputIcon, NewButton },
        inheritAttrs: false,
    })
    export default class CriterionEnumInput extends AbstractCriterionInput<JsonFormBase<JsonSchemaEnum>, any> {
        private faXmark = faXmark;
        private faQuestionCircle = faQuestionCircle;
        private options: DropdownOption[] = [];

        @Watch('form.schema.enum', { immediate: true })
        private onEnumChange(): void {
            const values = this.form.schema.enum || [];
            const labels = this.form.schema.enumLabels && this.form.schema.enumLabels.length > 0
                ? this.form.schema.enumLabels
                : null;
            this.options = labels
                ? values.map((value, i) => ({ value, label: labels[i] }))
                : values.map(this.buildOptionFromValue);
        }

        private get currentOption(): DropdownOption | null {
            const option = this.options.find((option) => option.value === this.value);
            return option || null;
        }

        private buildOptionFromValue(value: any): DropdownOption {
            return { value, label: value };
        }

        private onInput(option: DropdownOption): void {
            if (!option) {
                return;
            }
            this.$emit('input', option.value);
        }

        private onChange(option: DropdownOption): void {
            if (!option) {
                return;
            }
            this.$emit('change', option.value);
        }
    }
</script>
